import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { API_URL } from "../../Config";

const villaImg =
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=650&q=80";

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const parseAmount = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const num = parseFloat(String(val).replace(/,/g, ""));
  return isNaN(num) ? 0 : num;
};

const fmtCurrency = (val) => {
  const n = parseAmount(val);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

function DetailRow({ label, value }) {
  return (
    <tr>
      <td
        style={{
          paddingBottom: 16,
          fontSize: 13,
          letterSpacing: "1.5px",
          color: "#222222",
          fontWeight: "bold",
        }}
      >
        {label}
      </td>
      <td
        style={{
          paddingBottom: 16,
          fontSize: 13,
          letterSpacing: "1.5px",
          color: "#222222",
          textAlign: "right",
        }}
      >
        {value}
      </td>
    </tr>
  );
}

const Bookingconfirmpdf = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get("autoprint") === "1";

  useEffect(() => {
    if (!booking) return;

    const firstname = (booking.firstname || "guest").trim();
    const bookingId = booking.bookingid || booking.id || "NA";

    const safe = (s) =>
      String(s)
        .replace(/[\\/:*?"<>|]+/g, "")
        .replace(/\s+/g, " ")
        .trim();

    document.title = `${safe(firstname)}_${safe(bookingId)}_bookingconfirmation`;
  }, [booking]);

  useEffect(() => {
    if (!loading && booking && autoprint) {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [loading, booking, autoprint]);

  useEffect(() => {
    if (!autoprint) return;
    const handleAfterPrint = () => {
      window.close();
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [autoprint]);

  useEffect(() => {
    if (!id) return;

    const fetchBooking = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/booking/${id}`);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        setBooking(data);
      } catch (err) {
        console.error("Failed to fetch booking:", err);
        setError("Could not load booking details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e9e7e3",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        Loading booking details…
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          backgroundColor: "#e9e7e3",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        <p>{error || "Booking not found."}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "8px 20px",
            backgroundColor: "#2d2d2d",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  // ── Normalize propertydetails JSONB → props[] ──
  let rawDetails = booking.propertydetails;
  if (typeof rawDetails === "string") {
    try {
      rawDetails = JSON.parse(rawDetails);
    } catch {
      rawDetails = null;
    }
  }

  const hasDetailsObject =
    rawDetails && typeof rawDetails === "object" && !Array.isArray(rawDetails);

  const propertyNames = hasDetailsObject
    ? Object.keys(rawDetails)
    : booking.property
      ? [booking.property]
      : [];

  const props = propertyNames.map((name) => {
    const pd = (hasDetailsObject && rawDetails[name]) || {};
    return {
      property: pd.property || name,
      destination: pd.destination || booking.destination || "",
      guests: pd.guests || booking.guests || "",
      bedrooms: pd.bedrooms || booking.rooms || "",
      inclusions: pd.inclusions || booking.inclusions || "",
      checkin: pd.checkin || booking.checkin || "",
      checkout: pd.checkout || booking.checkout || "",
      checkin_time: pd.checkin_time || booking.checkintime || "",
      checkout_time: pd.checkout_time || booking.checkouttime || "",
      proprice: pd.proprice || booking.proprice || "",
      secdep: pd.secdep || booking.secdep || "",
      advpay: pd.advpay || booking.advpay || "",
      balpay: pd.balpay || booking.balpay || "",
      totpay: pd.totpay || booking.totpay || booking.finalprice || "",
    };
  });

  const isMulti = props.length > 1;

  const joinedProperties = props
    .map((p) => p.property)
    .filter(Boolean)
    .join(", ");

  const introPropertyLabel = joinedProperties || booking.property || "N/A";

  const grandTotal = props.reduce(
    (acc, p) => {
      acc.totpay += parseAmount(p.totpay);
      acc.advpay += parseAmount(p.advpay);
      acc.balpay += parseAmount(p.balpay);
      acc.secdep += parseAmount(p.secdep);
      return acc;
    },
    { totpay: 0, advpay: 0, balpay: 0, secdep: 0 },
  );

  // Reusable per-property booking details block
  const renderPropertyBlock = (p, index) => {
    // Force ALL property blocks to start on a new page
    const headerClass = "page-break-before";
    const headerLabel = isMulti
      ? `BOOKING DETAILS — ${p.property || ""}`
      : "BOOKING DETAILS";

    return (
      <React.Fragment key={index}>
        {/* Spacer (only visible in print) to give space from top margin */}
        <tr>
          <td className="pdf-top-spacer" height="18mm" colSpan="2" />
        </tr>

        {/* Booking Details Header */}
        <tr className={headerClass}>
          <td style={{ paddingBottom: 35 }}>
            <table width="100%" cellPadding="0" cellSpacing="0" border="0">
              <tbody>
                <tr>
                  <td width="30%">
                    <div
                      style={{
                        borderTop: "1px solid #3d3d3d",
                        width: "100%",
                        marginTop: "40px",
                      }}
                    />
                  </td>
                  <td
                    width="40%"
                    align="center"
                    style={{
                      fontSize: 20,
                      color: "#2d2d2d",
                      fontFamily: "Georgia, serif",
                      letterSpacing: "0.5px",
                      paddingTop: "40px",
                    }}
                  >
                    {headerLabel}
                  </td>
                  <td width="30%">
                    <div
                      style={{
                        borderTop: "1px solid #3d3d3d",
                        width: "100%",
                        marginTop: "40px",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>

        {/* Booking Details Rows */}
        <tr>
          <td>
            <table width="100%" cellPadding="0" cellSpacing="0" border="0">
              <tbody>
                <DetailRow label="PROPERTY NAME:" value={p.property || "N/A"} />
                <DetailRow
                  label="LOCATION:"
                  value={`${p.destination || "N/A"}, INDIA`}
                />
                <DetailRow
                  label="CHECK-IN DATE:"
                  value={p.checkin ? formatDate(p.checkin) : "N/A"}
                />
                <DetailRow
                  label="CHECK-OUT DATE:"
                  value={p.checkout ? formatDate(p.checkout) : "N/A"}
                />
                <DetailRow
                  label="NUMBER OF GUESTS:"
                  value={p.guests || "N/A"}
                />
                <DetailRow
                  label="BOOKING ID:"
                  value={`#${booking.bookingid || booking.id || "N/A"}`}
                />
                <DetailRow
                  label="SECURITY DEPOSIT :"
                  value={`₹${fmtCurrency(p.secdep) || "0.00"}`}
                />
                <DetailRow
                  label="TOTAL AMOUNT :"
                  value={`₹${fmtCurrency(p.totpay)}`}
                />
                <DetailRow
                  label="ADVANCE AMOUNT:"
                  value={`₹${fmtCurrency(p.advpay)}`}
                />
                <DetailRow
                  label="BALANCE DUE:"
                  value={`₹${fmtCurrency(p.balpay)}`}
                />

                {/* Spacer */}
                <tr>
                  <td colSpan="2" height="22" />
                </tr>

                <DetailRow
                  label="CHECK-IN TIME:"
                  value={p.checkin_time || "N/A"}
                />

                {/* Check-out time — no bottom padding */}
                <tr>
                  <td
                    style={{
                      fontSize: 13,
                      letterSpacing: "1.5px",
                      color: "#222222",
                      fontWeight: "bold",
                    }}
                  >
                    CHECK-OUT TIME:
                  </td>
                  <td
                    style={{
                      fontSize: 13,
                      letterSpacing: "1.5px",
                      color: "#222222",
                      textAlign: "right",
                    }}
                  >
                    {p.checkout_time || "N/A"}
                  </td>
                </tr>

                {/* Spacer */}
                <tr>
                  <td colSpan="2" height="40" />
                </tr>

                {/* Inclusion */}
                <tr>
                  <td
                    style={{
                      fontSize: 13,
                      letterSpacing: "1.5px",
                      color: "#222222",
                      fontWeight: "bold",
                    }}
                  >
                    INCLUSION:
                  </td>
                  <td
                    style={{
                      fontSize: 13,
                      letterSpacing: "1.5px",
                      color: "#222222",
                      textAlign: "right",
                    }}
                  >
                    {p.inclusions || "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        backgroundColor: "#e9e7e3",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      <style>{`
@page {
  size: A4;
  margin: 0;
}

@media print {
  html,
  body {
    background: #e7e6e2 !important;
    margin: 0 !important;
    padding: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Make the outer wrapper fill A4 with the cream background */
  .pdf-outer-wrap {
    width: 100% !important;
    max-width: none !important;
    background: #e9e7e3 !important;
    padding: 18mm 16mm !important;
    box-sizing: border-box !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Inner content keeps its natural (650px) centred width */
  .pdf-inner {
    width: 650px !important;
    max-width: 650px !important;
    margin: 0 auto !important;
  }

  .no-print {
    display: none !important;
  }

  .page-break-before {
    page-break-before: always;
    break-before: page;
  }

  .pdf-top-spacer {
    display: block;
    height: 18mm;
    width: 100%;
    font-size: 0;
    line-height: 0;
  }

  .avoid-break {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
`}</style>

      <table
        className="pdf-outer-wrap"
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{ backgroundColor: "#e9e7e3" }}
      >
        <tbody>
          <tr>
            <td align="center">
              {/* Main Wrapper */}
              <table
                className="pdf-inner"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{ width: 650, maxWidth: 650 }}
              >
                <tbody>
                  {/* Logo */}
                  <tr>
                    <td
                      align="center"
                      style={{ paddingBottom: 35, paddingTop: 35 }}
                    >
                      <img
                        src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9fc19416-6ee2-42fd-bc5a-4f0b5dc29800/public"
                        alt="Zorba Stays"
                        width="150"
                        style={{ display: "block" }}
                      />
                    </td>
                  </tr>
                  {/* Main Villa Image */}
                  <tr>
                    <td align="center">
                      <img
                        src={booking.villaImg || booking.villaimg || villaImg}
                        alt="Villa"
                        width="650"
                        style={{
                          display: "block",
                          width: "100%",
                          maxWidth: 650,
                        }}
                      />
                    </td>
                  </tr>
                  {/* Thank You */}
                  <tr>
                    <td
                      align="center"
                      style={{
                        paddingTop: 28,
                        fontSize: 11,
                        letterSpacing: 1,
                        color: "#444444",
                        fontFamily: "Georgia, serif",
                      }}
                    >
                      THANK YOU
                    </td>
                  </tr>
                  {/* Main Heading */}
                  <tr>
                    <td
                      align="center"
                      style={{
                        paddingTop: 8,
                        paddingBottom: 45,
                        fontSize: 22,
                        lineHeight: "34px",
                        color: "#2b2b2b",
                        fontFamily: "Georgia, serif",
                      }}
                    >
                      for choosing to stay at {introPropertyLabel},{" "}
                      {booking.destination || "N/A"}.
                    </td>
                  </tr>
                  {/* Greeting */}
                  <tr>
                    <td
                      style={{
                        paddingBottom: 25,
                        fontSize: 20,
                        color: "#222222",
                        fontWeight: "bold",
                        lineHeight: "30px",
                      }}
                    >
                      Dear {booking.firstname} {booking.lastname},
                    </td>
                  </tr>
                  {/* Intro Paragraph */}
                  <tr>
                    <td
                      style={{
                        paddingBottom: 35,
                        fontSize: 16,
                        lineHeight: "32px",
                        color: "#3d3d3d",
                      }}
                    >
                      Thank you for choosing Zorba Stays.
                      <br />
                      We're delighted to host you and are pleased to confirm
                      your upcoming stay with us.
                    </td>
                  </tr>
                  {/* ── One BOOKING DETAILS page per property ── */}
                  {props.map((p, idx) => renderPropertyBlock(p, idx))}
                  {/* ── Grand Total (multi-property only) ── */}
                  {isMulti && (
                    <>
                      <tr>
                        <td
                          className="pdf-top-spacer"
                          height="18mm"
                          colSpan="2"
                        />
                      </tr>
                      <tr className="page-break-before">
                        <td style={{ paddingBottom: 35 }}>
                          <table
                            width="100%"
                            cellPadding="0"
                            cellSpacing="0"
                            border="0"
                          >
                            <tbody>
                              <tr>
                                <td width="30%">
                                  <div
                                    style={{
                                      borderTop: "1px solid #3d3d3d",
                                      width: "100%",
                                      marginTop: "40px", //
                                    }}
                                  />
                                </td>

                                <td
                                  width="40%"
                                  align="center"
                                  style={{
                                    fontSize: 20,
                                    color: "#2d2d2d",
                                    fontFamily: "Georgia, serif",
                                    letterSpacing: "0.5px",
                                    paddingTop: "40px", //
                                  }}
                                >
                                  GRAND TOTAL
                                </td>
                                <td width="30%">
                                  <div
                                    style={{
                                      borderTop: "1px solid #3d3d3d",
                                      width: "100%",
                                      marginTop: "40px", //
                                    }}
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <table
                            width="100%"
                            cellPadding="0"
                            cellSpacing="0"
                            border="0"
                          >
                            <tbody>
                              <DetailRow
                                label="TOTAL SECURITY DEPOSIT:"
                                value={`₹${grandTotal.secdep.toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 2 },
                                )}`}
                              />
                              <DetailRow
                                label="TOTAL PAYABLE:"
                                value={`₹${grandTotal.totpay.toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 2 },
                                )}`}
                              />
                              <DetailRow
                                label="TOTAL ADVANCE:"
                                value={`₹${grandTotal.advpay.toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 2 },
                                )}`}
                              />
                              <DetailRow
                                label="TOTAL BALANCE DUE:"
                                value={`₹${grandTotal.balpay.toLocaleString(
                                  "en-IN",
                                  { maximumFractionDigits: 2 },
                                )}`}
                              />
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </>
                  )}
                  {/* Divider */}
                  <tr>
                    <td style={{ paddingTop: 38, paddingBottom: 25 }}>
                      <div
                        style={{
                          borderTop: "1px solid #3d3d3d",
                          width: "100%",
                        }}
                      />
                    </td>
                  </tr>
                  {/* CTA Buttons */}
                  <tr>
                    <td align="center" style={{ paddingBottom: 45 }}>
                      <table cellPadding="0" cellSpacing="0" border="0">
                        <tbody>
                          <tr>
                            <td style={{ paddingRight: 20 }}>
                              <a
                                href="https://zorbastays.com/plan-a-trip"
                                style={{
                                  border: "1px solid #2d2d2d",
                                  padding: "14px 32px",
                                  display: "inline-block",
                                  textDecoration: "none",
                                  color: "#2d2d2d",
                                  fontSize: 12,
                                  letterSpacing: "1.5px",
                                  fontWeight: "bold",
                                }}
                              >
                                TRAVEL GUIDE
                              </a>
                            </td>
                            <td>
                              <a
                                href="https://zorbastays.com/collaborate-with-us"
                                style={{
                                  border: "1px solid #2d2d2d",
                                  padding: "14px 32px",
                                  display: "inline-block",
                                  textDecoration: "none",
                                  color: "#2d2d2d",
                                  fontSize: 12,
                                  letterSpacing: "1.5px",
                                  fontWeight: "bold",
                                }}
                              >
                                CONCIERGE SERVICES
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  {/* ── Closing block (moved here, same page as Grand Total) ── */}
                  <tr>
                    <td
                      style={{
                        paddingBottom: 35,
                        fontSize: 16,
                        lineHeight: "32px",
                        color: "#3d3d3d",
                      }}
                    >
                      If you have any special requests, arrival timing updates,
                      or questions before your trip, please don't hesitate to
                      reach out.
                    </td>
                  </tr>
                  {/* Closing Paragraph */}
                  <tr>
                    <td
                      style={{
                        paddingBottom: 40,
                        fontSize: 16,
                        lineHeight: "32px",
                        color: "#3d3d3d",
                      }}
                    >
                      We look forward to welcoming you soon and ensuring your
                      stay with Zorba Stays is effortless and memorable.
                    </td>
                  </tr>
                  {/* Sign Off */}
                  <tr>
                    <td
                      style={{
                        paddingBottom: 70,
                        fontSize: 16,
                        lineHeight: "30px",
                        color: "#3d3d3d",
                      }}
                    >
                      Warm regards,
                      <br />
                      <strong>Team Zorba Stays</strong>
                    </td>
                  </tr>
                  {/* Social Icons */}
                  <tr>
                    <td align="center" style={{ paddingBottom: 45 }}>
                      <table cellPadding="0" cellSpacing="0" border="0">
                        <tbody>
                          <tr>
                            <td style={{ padding: "0 10px" }}>
                              <a
                                href="https://www.facebook.com/people/Zorba-Stays/61566287859705/"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <img
                                  src="https://cdn-icons-png.flaticon.com/128/20/20837.png"
                                  width="20"
                                  alt="Facebook icon"
                                  style={{
                                    display: "block",
                                    filter: "brightness(0)",
                                  }}
                                />
                              </a>
                            </td>
                            <td style={{ padding: "0 10px" }}>
                              <a
                                href="https://www.instagram.com/zorba_stays/?hl=en"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <img
                                  src="https://cdn-icons-png.flaticon.com/128/717/717392.png"
                                  width="20"
                                  alt="Instagram icon"
                                  style={{ display: "block" }}
                                />
                              </a>
                            </td>
                            <td style={{ padding: "0 10px" }}>
                              <a
                                href="https://youtube.com/@zorbastays?si=KOv29JvaK75A6HAD"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <img
                                  src="https://cdn-icons-png.flaticon.com/512/717/717426.png"
                                  width="22"
                                  alt="YouTube icon"
                                  style={{ display: "block" }}
                                />
                              </a>
                            </td>
                            <td style={{ padding: "0 10px" }}>
                              <a
                                href="https://api.whatsapp.com/send/?phone=916355231066&text&type=phone_number&app_absent=0"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <img
                                  src="https://cdn-icons-png.flaticon.com/128/1384/1384023.png"
                                  width="20"
                                  alt="WhatsApp icon"
                                  style={{ display: "block" }}
                                />
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  {/* Bottom Footer */}
                  <tr>
                    <td>
                      <table
                        width="100%"
                        cellPadding="0"
                        cellSpacing="0"
                        border="0"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <tbody>
                          <tr>
                            <td style={{ padding: "35px 30px" }}>
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                              >
                                <tbody>
                                  <tr>
                                    <td width="28%" valign="top">
                                      <img
                                        src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/7c57c4a2-564b-4b83-62b0-761f834b5c00/public"
                                        width="130"
                                        alt="Zorba Stays logo"
                                      />
                                    </td>
                                    <td
                                      align="center"
                                      valign="top"
                                      style={{
                                        fontSize: 14,
                                        color: "#2d2d2d",
                                        lineHeight: "28px",
                                        textDecoration: "underline",
                                        textUnderlineOffset: 4,
                                      }}
                                    >
                                      <a
                                        href="https://zorbastays.com/collaborate-with-us"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          textDecoration: "none",
                                          color: "#2d2d2d",
                                        }}
                                      >
                                        Contact Us
                                      </a>
                                    </td>
                                    <td
                                      align="center"
                                      valign="top"
                                      style={{
                                        fontSize: 14,
                                        color: "#2d2d2d",
                                        lineHeight: "28px",
                                        textDecoration: "underline",
                                        textUnderlineOffset: 4,
                                      }}
                                    >
                                      <a
                                        href="https://zorbastays.com/experience/wildlife-safari-experience"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          textDecoration: "none",
                                          color: "#2d2d2d",
                                        }}
                                      >
                                        Experiences
                                      </a>
                                    </td>
                                    <td
                                      align="center"
                                      valign="top"
                                      style={{
                                        fontSize: 14,
                                        color: "#2d2d2d",
                                        lineHeight: "28px",
                                        textDecoration: "underline",
                                        textUnderlineOffset: 4,
                                      }}
                                    >
                                      <a
                                        href="https://zorbastays.com/destination/goa"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          textDecoration: "none",
                                          color: "#2d2d2d",
                                        }}
                                      >
                                        Destinations
                                      </a>
                                    </td>
                                    <td
                                      align="center"
                                      valign="top"
                                      style={{
                                        fontSize: 14,
                                        color: "#2d2d2d",
                                        lineHeight: "28px",
                                        textDecoration: "underline",
                                        textUnderlineOffset: 4,
                                      }}
                                    >
                                      <a
                                        href="https://zorbastays.com/blogs"
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          textDecoration: "none",
                                          color: "#2d2d2d",
                                        }}
                                      >
                                        Travel Stories
                                      </a>
                                    </td>
                                  </tr>

                                  <tr>
                                    <td colSpan="5" height="40" />
                                  </tr>

                                  <tr>
                                    <td
                                      colSpan="5"
                                      style={{
                                        fontSize: 12,
                                        lineHeight: "24px",
                                        color: "#444444",
                                      }}
                                    >
                                      Zorba Stays is registered in India,
                                      Registered company number:90167 72761,
                                      Registered address: 308 - Business
                                      Centrum, Amin Marg, Rajkot, India
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Bookingconfirmpdf;
// import React from "react";

// const villaImg =
//   "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=650&q=80";

// function formatDate(dateStr) {
//   const date = new Date(dateStr);
//   return date.toLocaleDateString("en-IN", {
//     day: "2-digit",
//     month: "long",
//     year: "numeric",
//   });
// }

// function DetailRow({ label, value }) {
//   return (
//     <tr>
//       <td
//         style={{
//           paddingBottom: 16,
//           fontSize: 13,
//           letterSpacing: "1.5px",
//           color: "#222222",
//           fontWeight: "bold",
//         }}
//       >
//         {label}
//       </td>
//       <td
//         style={{
//           paddingBottom: 16,
//           fontSize: 13,
//           letterSpacing: "1.5px",
//           color: "#222222",
//           textAlign: "right",
//         }}
//       >
//         {value}
//       </td>
//     </tr>
//   );
// }

// const Bookingconfirmpdf = ({ booking = {} }) => {
//   return (
//     <div
//       style={{
//         margin: 0,
//         padding: 0,
//         backgroundColor: "#e9e7e3",
//         fontFamily: "Arial, Helvetica, sans-serif",
//       }}
//     >
//       <table
//         width="100%"
//         cellPadding="0"
//         cellSpacing="0"
//         border="0"
//         style={{ backgroundColor: "#e9e7e3" }}
//       >
//         <tbody>
//           <tr>
//             <td align="center" style={{ padding: "40px 20px" }}>
//               {/* Main Wrapper */}
//               <table
//                 cellPadding="0"
//                 cellSpacing="0"
//                 border="0"
//                 style={{ width: 650, maxWidth: 650 }}
//               >
//                 <tbody>
//                   {/* Logo */}
//                   <tr>
//                     <td align="center" style={{ paddingBottom: 35 }}>
//                       <img
//                         src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9fc19416-6ee2-42fd-bc5a-4f0b5dc29800/public"
//                         alt="Zorba Stays"
//                         width="150"
//                         style={{ display: "block" }}
//                       />
//                     </td>
//                   </tr>

//                   {/* Main Villa Image */}
//                   <tr>
//                     <td align="center">
//                       <img
//                         src={booking.villaImg || villaImg}
//                         alt="Villa Image"
//                         width="650"
//                         style={{
//                           display: "block",
//                           width: "100%",
//                           maxWidth: 650,
//                         }}
//                       />
//                     </td>
//                   </tr>

//                   {/* Thank You */}
//                   <tr>
//                     <td
//                       align="center"
//                       style={{
//                         paddingTop: 28,
//                         fontSize: 11,
//                         letterSpacing: 1,
//                         color: "#444444",
//                         fontFamily: "Georgia, serif",
//                       }}
//                     >
//                       THANK YOU
//                     </td>
//                   </tr>

//                   {/* Main Heading */}
//                   <tr>
//                     <td
//                       align="center"
//                       style={{
//                         paddingTop: 8,
//                         paddingBottom: 45,
//                         fontSize: 22,
//                         lineHeight: "34px",
//                         color: "#2b2b2b",
//                         fontFamily: "Georgia, serif",
//                       }}
//                     >
//                       for choosing to stay at {booking.property || "N/A"},{" "}
//                       {booking.destination || "N/A"}.
//                     </td>
//                   </tr>

//                   {/* Greeting */}
//                   <tr>
//                     <td
//                       style={{
//                         paddingBottom: 25,
//                         fontSize: 20,
//                         color: "#222222",
//                         fontWeight: "bold",
//                         lineHeight: "30px",
//                       }}
//                     >
//                       Dear {booking.firstname} {booking.lastname},
//                     </td>
//                   </tr>

//                   {/* Intro Paragraph */}
//                   <tr>
//                     <td
//                       style={{
//                         paddingBottom: 35,
//                         fontSize: 16,
//                         lineHeight: "32px",
//                         color: "#3d3d3d",
//                       }}
//                     >
//                       Thank you for choosing Zorba Stays.
//                       <br />
//                       We're delighted to host you and are pleased to confirm
//                       your upcoming stay with us.
//                     </td>
//                   </tr>

//                   {/* Booking Details Header */}
//                   <tr>
//                     <td style={{ paddingBottom: 35 }}>
//                       <table
//                         width="100%"
//                         cellPadding="0"
//                         cellSpacing="0"
//                         border="0"
//                       >
//                         <tbody>
//                           <tr>
//                             <td width="30%">
//                               <div
//                                 style={{
//                                   borderTop: "1px solid #3d3d3d",
//                                   width: "100%",
//                                 }}
//                               />
//                             </td>
//                             <td
//                               width="40%"
//                               align="center"
//                               style={{
//                                 fontSize: 20,
//                                 color: "#2d2d2d",
//                                 fontFamily: "Georgia, serif",
//                                 letterSpacing: "0.5px",
//                               }}
//                             >
//                               BOOKING DETAILS
//                             </td>
//                             <td width="30%">
//                               <div
//                                 style={{
//                                   borderTop: "1px solid #3d3d3d",
//                                   width: "100%",
//                                 }}
//                               />
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </td>
//                   </tr>

//                   {/* Booking Details Rows */}
//                   <tr>
//                     <td>
//                       <table
//                         width="100%"
//                         cellPadding="0"
//                         cellSpacing="0"
//                         border="0"
//                       >
//                         <tbody>
//                           <DetailRow
//                             label="PROPERTY NAME:"
//                             value={booking.property || "N/A"}
//                           />
//                           <DetailRow
//                             label="LOCATION:"
//                             value={`${booking.destination || "N/A"}, INDIA`}
//                           />
//                           <DetailRow
//                             label="CHECK-IN DATE:"
//                             value={
//                               booking.checkin
//                                 ? formatDate(booking.checkin)
//                                 : "N/A"
//                             }
//                           />
//                           <DetailRow
//                             label="CHECK-OUT DATE:"
//                             value={
//                               booking.checkout
//                                 ? formatDate(booking.checkout)
//                                 : "N/A"
//                             }
//                           />
//                           <DetailRow
//                             label="NUMBER OF GUESTS:"
//                             value={booking.guests || "N/A"}
//                           />
//                           <DetailRow
//                             label="BOOKING ID:"
//                             value={`#${booking.id || "N/A"}`}
//                           />
//                           <DetailRow
//                             label="SECURITY DEPOSIT :"
//                             value={`₹${booking.secdep || "N/A"}`}
//                           />
//                           <DetailRow
//                             label="TOTAL AMOUNT :"
//                             value={`₹${booking.totpay || booking.finalprice || "N/A"}`}
//                           />
//                           <DetailRow
//                             label="ADVANCE AMOUNT:"
//                             value={`₹${booking.advpay || "N/A"}`}
//                           />
//                           <DetailRow
//                             label="BALANCE DUE:"
//                             value={`₹${booking.balpay || "N/A"}`}
//                           />

//                           {/* Spacer */}
//                           <tr>
//                             <td colSpan="2" height="22" />
//                           </tr>

//                           <DetailRow
//                             label="CHECK-IN TIME:"
//                             value={booking.checkintime || "N/A"}
//                           />

//                           {/* Check-out time — no bottom padding */}
//                           <tr>
//                             <td
//                               style={{
//                                 fontSize: 13,
//                                 letterSpacing: "1.5px",
//                                 color: "#222222",
//                                 fontWeight: "bold",
//                               }}
//                             >
//                               CHECK-OUT TIME:
//                             </td>
//                             <td
//                               style={{
//                                 fontSize: 13,
//                                 letterSpacing: "1.5px",
//                                 color: "#222222",
//                                 textAlign: "right",
//                               }}
//                             >
//                               {booking.checkouttime || "N/A"}
//                             </td>
//                           </tr>

//                           {/* Spacer */}
//                           <tr>
//                             <td colSpan="2" height="40" />
//                           </tr>

//                           {/* Inclusion */}
//                           <tr>
//                             <td
//                               style={{
//                                 fontSize: 13,
//                                 letterSpacing: "1.5px",
//                                 color: "#222222",
//                                 fontWeight: "bold",
//                               }}
//                             >
//                               INCLUSION:
//                             </td>
//                             <td
//                               style={{
//                                 fontSize: 13,
//                                 letterSpacing: "1.5px",
//                                 color: "#222222",
//                                 textAlign: "right",
//                               }}
//                             >
//                               {booking.inclusions || "N/A"}
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </td>
//                   </tr>

//                   {/* Divider */}
//                   <tr>
//                     <td style={{ paddingTop: 38, paddingBottom: 25 }}>
//                       <div
//                         style={{
//                           borderTop: "1px solid #3d3d3d",
//                           width: "100%",
//                         }}
//                       />
//                     </td>
//                   </tr>

//                   {/* CTA Buttons */}
//                   <tr>
//                     <td align="center" style={{ paddingBottom: 45 }}>
//                       <table cellPadding="0" cellSpacing="0" border="0">
//                         <tbody>
//                           <tr>
//                             <td style={{ paddingRight: 20 }}>
//                               <a
//                                 href="https://zorbastays.com/plan-trip"
//                                 style={{
//                                   border: "1px solid #2d2d2d",
//                                   padding: "14px 32px",
//                                   display: "inline-block",
//                                   textDecoration: "none",
//                                   color: "#2d2d2d",
//                                   fontSize: 12,
//                                   letterSpacing: "1.5px",
//                                   fontWeight: "bold",
//                                 }}
//                               >
//                                 TRAVEL GUIDE
//                               </a>
//                             </td>
//                             <td>
//                               <a
//                                 href="https://zorbastays.com/contact"
//                                 style={{
//                                   border: "1px solid #2d2d2d",
//                                   padding: "14px 32px",
//                                   display: "inline-block",
//                                   textDecoration: "none",
//                                   color: "#2d2d2d",
//                                   fontSize: 12,
//                                   letterSpacing: "1.5px",
//                                   fontWeight: "bold",
//                                 }}
//                               >
//                                 CONCIERGE serviceS
//                               </a>
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </td>
//                   </tr>

//                   {/* Contact Paragraph */}
//                   <tr>
//                     <td
//                       style={{
//                         paddingBottom: 35,
//                         fontSize: 16,
//                         lineHeight: "32px",
//                         color: "#3d3d3d",
//                       }}
//                     >
//                       If you have any special requests, arrival timing updates,
//                       or questions before your trip, please don't hesitate to
//                       reach out.
//                     </td>
//                   </tr>

//                   {/* Closing Paragraph */}
//                   <tr>
//                     <td
//                       style={{
//                         paddingBottom: 40,
//                         fontSize: 16,
//                         lineHeight: "32px",
//                         color: "#3d3d3d",
//                       }}
//                     >
//                       We look forward to welcoming you soon and ensuring your
//                       stay with Zorba Stays is effortless and memorable.
//                     </td>
//                   </tr>

//                   {/* Sign Off */}
//                   <tr>
//                     <td
//                       style={{
//                         paddingBottom: 70,
//                         fontSize: 16,
//                         lineHeight: "30px",
//                         color: "#3d3d3d",
//                       }}
//                     >
//                       Warm regards,
//                       <br />
//                       <strong>Team Zorba Stays</strong>
//                     </td>
//                   </tr>

//                   {/* Social Icons */}
//                   <tr>
//                     <td align="center" style={{ paddingBottom: 45 }}>
//                       <table cellPadding="0" cellSpacing="0" border="0">
//                         <tbody>
//                           <tr>
//                             <td style={{ padding: "0 10px" }}>
//                               <a
//                                 href="https://www.facebook.com/people/Zorba-Stays/61566287859705/"
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 style={{ textDecoration: "none" }}
//                               >
//                                 <img
//                                   src="https://cdn-icons-png.flaticon.com/128/20/20837.png"
//                                   width="20"
//                                   alt="Facebook"
//                                   style={{
//                                     display: "block",
//                                     filter: "brightness(0)",
//                                   }}
//                                 />
//                               </a>
//                             </td>
//                             <td style={{ padding: "0 10px" }}>
//                               <a
//                                 href="https://www.instagram.com/zorba_stays/?hl=en"
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 style={{ textDecoration: "none" }}
//                               >
//                                 <img
//                                   src="https://cdn-icons-png.flaticon.com/128/717/717392.png"
//                                   width="20"
//                                   alt="Instagram"
//                                   style={{ display: "block" }}
//                                 />
//                               </a>
//                             </td>
//                             <td style={{ padding: "0 10px" }}>
//                               <a
//                                 href="https://youtube.com/@zorbastays?si=KOv29JvaK75A6HAD"
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 style={{ textDecoration: "none" }}
//                               >
//                                 <img
//                                   src="https://cdn-icons-png.flaticon.com/512/717/717426.png"
//                                   width="22"
//                                   alt="YouTube"
//                                   style={{ display: "block" }}
//                                 />
//                               </a>
//                             </td>
//                             <td style={{ padding: "0 10px" }}>
//                               <a
//                                 href="https://api.whatsapp.com/send/?phone=916355231066&text&type=phone_number&app_absent=0"
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 style={{ textDecoration: "none" }}
//                               >
//                                 <img
//                                   src="https://cdn-icons-png.flaticon.com/128/1384/1384023.png"
//                                   width="20"
//                                   alt="WhatsApp"
//                                   style={{ display: "block" }}
//                                 />
//                               </a>
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </td>
//                   </tr>

//                   {/* Bottom Footer */}
//                   <tr>
//                     <td>
//                       <table
//                         width="100%"
//                         cellPadding="0"
//                         cellSpacing="0"
//                         border="0"
//                         style={{ backgroundColor: "#ffffff" }}
//                       >
//                         <tbody>
//                           <tr>
//                             <td style={{ padding: "35px 30px" }}>
//                               <table
//                                 width="100%"
//                                 cellPadding="0"
//                                 cellSpacing="0"
//                                 border="0"
//                               >
//                                 <tbody>
//                                   <tr>
//                                     <td width="28%" valign="top">
//                                       <img
//                                         src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/7c57c4a2-564b-4b83-62b0-761f834b5c00/public"
//                                         width="130"
//                                         alt="Zorba Stays"
//                                       />
//                                     </td>
//                                     <td
//                                       align="center"
//                                       valign="top"
//                                       style={{
//                                         fontSize: 14,
//                                         color: "#2d2d2d",
//                                         lineHeight: "28px",
//                                         textDecoration: "underline",
//                                         textUnderlineOffset: 4,
//                                       }}
//                                     >
//                                       <a
//                                         href="https://zorbastays.com/contact"
//                                         target="_blank"
//                                         rel="noreferrer"
//                                         style={{
//                                           textDecoration: "none",
//                                           color: "#2d2d2d",
//                                         }}
//                                       >
//                                         Contact Us
//                                       </a>
//                                     </td>
//                                     <td
//                                       align="center"
//                                       valign="top"
//                                       style={{
//                                         fontSize: 14,
//                                         color: "#2d2d2d",
//                                         lineHeight: "28px",
//                                         textDecoration: "underline",
//                                         textUnderlineOffset: 4,
//                                       }}
//                                     >
//                                       <a
//                                         href="https://zorbastays.com/experiences?experience=Wildlife%20Safari%20Experience"
//                                         target="_blank"
//                                         rel="noreferrer"
//                                         style={{
//                                           textDecoration: "none",
//                                           color: "#2d2d2d",
//                                         }}
//                                       >
//                                         Experiences
//                                       </a>
//                                     </td>
//                                     <td
//                                       align="center"
//                                       valign="top"
//                                       style={{
//                                         fontSize: 14,
//                                         color: "#2d2d2d",
//                                         lineHeight: "28px",
//                                         textDecoration: "underline",
//                                         textUnderlineOffset: 4,
//                                       }}
//                                     >
//                                       <a
//                                         href="https://zorbastays.com/destinations?place=Goa"
//                                         target="_blank"
//                                         rel="noreferrer"
//                                         style={{
//                                           textDecoration: "none",
//                                           color: "#2d2d2d",
//                                         }}
//                                       >
//                                         destinations
//                                       </a>
//                                     </td>
//                                     <td
//                                       align="center"
//                                       valign="top"
//                                       style={{
//                                         fontSize: 14,
//                                         color: "#2d2d2d",
//                                         lineHeight: "28px",
//                                         textDecoration: "underline",
//                                         textUnderlineOffset: 4,
//                                       }}
//                                     >
//                                       <a
//                                         href="https://zorbastays.com/blogs"
//                                         target="_blank"
//                                         rel="noreferrer"
//                                         style={{
//                                           textDecoration: "none",
//                                           color: "#2d2d2d",
//                                         }}
//                                       >
//                                         Travel Stories
//                                       </a>
//                                     </td>
//                                   </tr>

//                                   <tr>
//                                     <td colSpan="5" height="40" />
//                                   </tr>

//                                   <tr>
//                                     <td
//                                       colSpan="5"
//                                       style={{
//                                         fontSize: 12,
//                                         lineHeight: "24px",
//                                         color: "#444444",
//                                       }}
//                                     >
//                                       This email was intended for{" "}
//                                       {booking.email}. Zorba Stays is registered
//                                       in India.
//                                       <br />
//                                       Registered company number:90167 72761.
//                                       Registered address: 308 - Business
//                                       Centrum, Amin Marg, Rajkot, India
//                                     </td>
//                                   </tr>
//                                 </tbody>
//                               </table>
//                             </td>
//                           </tr>
//                         </tbody>
//                       </table>
//                     </td>
//                   </tr>
//                 </tbody>
//               </table>
//             </td>
//           </tr>
//         </tbody>
//       </table>

//       {/* Attachments Note */}
//       <table
//         width="100%"
//         cellPadding="0"
//         cellSpacing="0"
//         border="0"
//         style={{ marginTop: 30 }}
//       >
//         <tbody>
//           <tr>
//             <td
//               style={{
//                 fontSize: 13,
//                 color: "#444444",
//                 lineHeight: "22px",
//                 padding: "16px 20px",
//                 backgroundColor: "#f5f4f0",
//                 borderLeft: "3px solid #2d2d2d",
//               }}
//             >
//               📎 Kindly take a moment to read through the House Rules and Terms
//               & Conditions mentioned below to help ensure a smooth and pleasant
//               stay.
//             </td>
//           </tr>
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default Bookingconfirmpdf;
// import React from "react";

// const formatDate = (d) => {
//   if (!d) return "N/A";
//   return new Date(d).toLocaleDateString("en-IN", {
//     day: "2-digit",
//     month: "long",
//     year: "numeric",
//   });
// };

// /**
//  * BookingConfirmPDF
//  * Props:
//  *   booking  – booking object (same shape as your DB row)
//  *   villaImg – resolved banner image URL (fetch before passing in, or pass DEFAULT)
//  *   onClose  – optional callback to close/unmount
//  *   onPrint  – optional override; defaults to window.print()
//  */
// const BookingConfirmPDF = ({ booking = {}, villaImg, onClose, onPrint }) => {
//   const DEFAULT_IMG =
//     "https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/10517686-a4ba-4a92-0273-6b43eb6ed300/public";

//   const img = villaImg || DEFAULT_IMG;

//   const handlePrint = () => (onPrint ? onPrint() : window.print());

//   const Row = ({ label, value }) => (
//     <tr>
//       <td
//         style={{
//           paddingBottom: 16,
//           fontSize: 13,
//           letterSpacing: "1.5px",
//           color: "#222222",
//           fontWeight: "bold",
//         }}
//       >
//         {label}
//       </td>
//       <td
//         align="right"
//         style={{
//           paddingBottom: 16,
//           fontSize: 13,
//           letterSpacing: "1.5px",
//           color: "#222222",
//         }}
//       >
//         {value}
//       </td>
//     </tr>
//   );

//   return (
//     <>
//       <style>{`
//         @media print {
//           .no-print { display: none !important; }
//           .pdf-no-break { page-break-inside: avoid; }
//           .pdf-tight { margin-bottom: 15px !important; }
//           body { background-color: #e9e7e3 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
//         }
//         @page { margin: 15mm; size: A4; }
//       `}</style>

//       {/* ── Action bar ── */}
//       <div
//         className="no-print"
//         style={{
//           position: "sticky",
//           top: 0,
//           zIndex: 100,
//           backgroundColor: "#1E2827",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           padding: "10px 24px",
//           gap: 12,
//         }}
//       >
//         <span style={{ color: "#e7e6e2", fontWeight: "bold", fontSize: 15 }}>
//           Booking Confirmation — {booking.firstname} {booking.lastname}
//         </span>
//         <div style={{ display: "flex", gap: 10 }}>
//           <button
//             onClick={handlePrint}
//             style={{
//               padding: "8px 20px",
//               backgroundColor: "#e7e6e2",
//               color: "#1E2827",
//               border: "none",
//               borderRadius: 6,
//               fontWeight: "bold",
//               fontSize: 13,
//               cursor: "pointer",
//             }}
//           >
//             🖨 Print / Save PDF
//           </button>
//           {onClose && (
//             <button
//               onClick={onClose}
//               style={{
//                 padding: "8px 16px",
//                 backgroundColor: "transparent",
//                 color: "#e7e6e2",
//                 border: "1px solid #e7e6e2",
//                 borderRadius: 6,
//                 fontSize: 13,
//                 cursor: "pointer",
//               }}
//             >
//               ✕ Close
//             </button>
//           )}
//         </div>
//       </div>

//       {/* ── Document body ── */}
//       <div
//         style={{
//           margin: 0,
//           padding: 0,
//           backgroundColor: "#e9e7e3",
//           fontFamily: "Arial, Helvetica, sans-serif",
//         }}
//       >
//         <table
//           width="100%"
//           cellPadding={0}
//           cellSpacing={0}
//           border={0}
//           style={{ backgroundColor: "#e9e7e3" }}
//         >
//           <tbody>
//             <tr>
//               <td align="center" style={{ padding: "40px 20px" }}>
//                 <table
//                   width="650"
//                   cellPadding={0}
//                   cellSpacing={0}
//                   border={0}
//                   style={{ width: 650, maxWidth: 650 }}
//                 >
//                   <tbody>
//                     {/* Logo */}
//                     <tr>
//                       <td align="center" style={{ paddingBottom: 35 }}>
//                         <img
//                           src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/9fc19416-6ee2-42fd-bc5a-4f0b5dc29800/public"
//                           alt="Zorba Stays"
//                           width={150}
//                           style={{ display: "block" }}
//                         />
//                       </td>
//                     </tr>

//                     {/* Villa image */}
//                     <tr>
//                       <td align="center">
//                         <img
//                           src={img}
//                           alt="Villa"
//                           width={650}
//                           style={{
//                             display: "block",
//                             width: "100%",
//                             maxWidth: 650,
//                           }}
//                         />
//                       </td>
//                     </tr>

//                     {/* Thank you */}
//                     <tr>
//                       <td
//                         align="center"
//                         style={{
//                           paddingTop: 28,
//                           fontSize: 11,
//                           letterSpacing: 1,
//                           color: "#444444",
//                           fontFamily: "Georgia, serif",
//                         }}
//                       >
//                         THANK YOU
//                       </td>
//                     </tr>

//                     {/* Heading */}
//                     <tr>
//                       <td
//                         align="center"
//                         style={{
//                           paddingTop: 8,
//                           paddingBottom: 45,
//                           fontSize: 22,
//                           lineHeight: "34px",
//                           color: "#2b2b2b",
//                           fontFamily: "Georgia, serif",
//                         }}
//                       >
//                         for choosing to stay at {booking.property || "N/A"},{" "}
//                         {booking.destination || "N/A"}.
//                       </td>
//                     </tr>

//                     {/* Greeting */}
//                     <tr>
//                       <td
//                         style={{
//                           paddingBottom: 25,
//                           fontSize: 20,
//                           color: "#222222",
//                           fontWeight: "bold",
//                           lineHeight: "30px",
//                         }}
//                       >
//                         Dear {booking.firstname} {booking.lastname},
//                       </td>
//                     </tr>

//                     {/* Intro */}
//                     <tr>
//                       <td
//                         style={{
//                           paddingBottom: 35,
//                           fontSize: 16,
//                           lineHeight: "32px",
//                           color: "#3d3d3d",
//                         }}
//                       >
//                         Thank you for choosing Zorba Stays.
//                         <br />
//                         We're delighted to host you and are pleased to confirm
//                         your upcoming stay with us.
//                       </td>
//                     </tr>

//                     {/* BOOKING DETAILS header */}
//                     <tr>
//                       <td style={{ paddingBottom: 35 }}>
//                         <table
//                           width="100%"
//                           cellPadding={0}
//                           cellSpacing={0}
//                           border={0}
//                         >
//                           <tbody>
//                             <tr>
//                               <td width="30%">
//                                 <div
//                                   style={{
//                                     borderTop: "1px solid #3d3d3d",
//                                     width: "100%",
//                                   }}
//                                 />
//                               </td>
//                               <td
//                                 width="40%"
//                                 align="center"
//                                 style={{
//                                   fontSize: 20,
//                                   color: "#2d2d2d",
//                                   fontFamily: "Georgia, serif",
//                                   letterSpacing: "0.5px",
//                                 }}
//                               >
//                                 BOOKING DETAILS
//                               </td>
//                               <td width="30%">
//                                 <div
//                                   style={{
//                                     borderTop: "1px solid #3d3d3d",
//                                     width: "100%",
//                                   }}
//                                 />
//                               </td>
//                             </tr>
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                     {/* Detail rows */}
//                     <tr>
//                       <td>
//                         <table
//                           width="100%"
//                           cellPadding={0}
//                           cellSpacing={0}
//                           border={0}
//                           className="pdf-no-break"
//                         >
//                           <tbody>
//                             <Row
//                               label="PROPERTY NAME:"
//                               value={booking.property || "N/A"}
//                             />
//                             <Row
//                               label="LOCATION:"
//                               value={`${booking.destination || "N/A"}, INDIA`}
//                             />
//                             <Row
//                               label="CHECK-IN DATE:"
//                               value={formatDate(booking.checkin)}
//                             />
//                             <Row
//                               label="CHECK-OUT DATE:"
//                               value={formatDate(booking.checkout)}
//                             />
//                             <Row
//                               label="NUMBER OF GUESTS:"
//                               value={booking.guests || "N/A"}
//                             />
//                             <Row
//                               label="BOOKING ID:"
//                               value={`#${booking.id || "N/A"}`}
//                             />
//                             <Row
//                               label="SECURITY DEPOSIT:"
//                               value={`₹${booking.secdep || "N/A"}`}
//                             />
//                             <Row
//                               label="TOTAL AMOUNT:"
//                               value={`₹${booking.totpay || booking.finalprice || "N/A"}`}
//                             />
//                             <Row
//                               label="ADVANCE AMOUNT:"
//                               value={`₹${booking.advpay || "N/A"}`}
//                             />
//                             <Row
//                               label="BALANCE DUE:"
//                               value={`₹${booking.balpay || "N/A"}`}
//                             />
//                             <tr>
//                               <td colSpan={2} height={22} />
//                             </tr>
//                             <Row
//                               label="CHECK-IN TIME:"
//                               value={booking.checkintime || "N/A"}
//                             />
//                             <Row
//                               label="CHECK-OUT TIME:"
//                               value={booking.checkouttime || "N/A"}
//                             />
//                             <tr>
//                               <td colSpan={2} height={40} />
//                             </tr>
//                             <Row
//                               label="INCLUSION:"
//                               value={booking.inclusions || "N/A"}
//                             />
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                     {/* Divider */}
//                     <tr>
//                       <td style={{ paddingTop: 38, paddingBottom: 25 }}>
//                         <div
//                           style={{
//                             borderTop: "1px solid #3d3d3d",
//                             width: "100%",
//                           }}
//                         />
//                       </td>
//                     </tr>

//                     {/* CTA buttons */}
//                     <tr>
//                       <td align="center" style={{ paddingBottom: 45 }}>
//                         <table cellPadding={0} cellSpacing={0} border={0}>
//                           <tbody>
//                             <tr>
//                               <td style={{ paddingRight: 20 }}>
//                                 <a
//                                   href="https://zorbastays.com/plan-trip"
//                                   style={{
//                                     border: "1px solid #2d2d2d",
//                                     padding: "14px 32px",
//                                     display: "inline-block",
//                                     textDecoration: "none",
//                                     color: "#2d2d2d",
//                                     fontSize: 12,
//                                     letterSpacing: "1.5px",
//                                     fontWeight: "bold",
//                                   }}
//                                 >
//                                   TRAVEL GUIDE
//                                 </a>
//                               </td>
//                               <td>
//                                 <a
//                                   href="https://zorbastays.com/contact"
//                                   style={{
//                                     border: "1px solid #2d2d2d",
//                                     padding: "14px 32px",
//                                     display: "inline-block",
//                                     textDecoration: "none",
//                                     color: "#2d2d2d",
//                                     fontSize: 12,
//                                     letterSpacing: "1.5px",
//                                     fontWeight: "bold",
//                                   }}
//                                 >
//                                   CONCIERGE serviceS
//                                 </a>
//                               </td>
//                             </tr>
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                     {/* Body copy */}
//                     <tr>
//                       <td
//                         style={{
//                           paddingBottom: 35,
//                           fontSize: 16,
//                           lineHeight: "32px",
//                           color: "#3d3d3d",
//                         }}
//                       >
//                         If you have any special requests, arrival timing
//                         updates, or questions before your trip, please don't
//                         hesitate to reach out.
//                       </td>
//                     </tr>
//                     <tr>
//                       <td
//                         style={{
//                           paddingBottom: 40,
//                           fontSize: 16,
//                           lineHeight: "32px",
//                           color: "#3d3d3d",
//                         }}
//                       >
//                         We look forward to welcoming you soon and ensuring your
//                         stay with Zorba Stays is effortless and memorable.
//                       </td>
//                     </tr>
//                     <tr>
//                       <td
//                         style={{
//                           paddingBottom: 70,
//                           fontSize: 16,
//                           lineHeight: "30px",
//                           color: "#3d3d3d",
//                         }}
//                       >
//                         Warm regards,
//                         <br />
//                         <strong>Team Zorba Stays</strong>
//                       </td>
//                     </tr>

//                     {/* Social icons */}
//                     <tr>
//                       <td align="center" style={{ paddingBottom: 45 }}>
//                         <table cellPadding={0} cellSpacing={0} border={0}>
//                           <tbody>
//                             <tr>
//                               {[
//                                 {
//                                   href: "https://www.facebook.com/people/Zorba-Stays/61566287859705/",
//                                   src: "https://cdn-icons-png.flaticon.com/128/20/20837.png",
//                                   width: 20,
//                                   style: {
//                                     display: "block",
//                                     filter: "brightness(0)",
//                                   },
//                                 },
//                                 {
//                                   href: "https://www.instagram.com/zorba_stays/?hl=en",
//                                   src: "https://cdn-icons-png.flaticon.com/128/717/717392.png",
//                                   width: 20,
//                                   style: { display: "block" },
//                                 },
//                                 {
//                                   href: "https://youtube.com/@zorbastays",
//                                   src: "https://cdn-icons-png.flaticon.com/512/717/717426.png",
//                                   width: 22,
//                                   style: { display: "block" },
//                                 },
//                                 {
//                                   href: "https://api.whatsapp.com/send/?phone=916355231066",
//                                   src: "https://cdn-icons-png.flaticon.com/128/1384/1384023.png",
//                                   width: 20,
//                                   style: { display: "block" },
//                                 },
//                               ].map(
//                                 ({ href, src, width, style: imgStyle }, i) => (
//                                   <td key={i} style={{ padding: "0 10px" }}>
//                                     <a
//                                       href={href}
//                                       target="_blank"
//                                       rel="noreferrer"
//                                       style={{ textDecoration: "none" }}
//                                     >
//                                       <img
//                                         src={src}
//                                         width={width}
//                                         style={imgStyle}
//                                         alt=""
//                                       />
//                                     </a>
//                                   </td>
//                                 ),
//                               )}
//                             </tr>
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                     {/* Bottom footer */}
//                     <tr>
//                       <td>
//                         <table
//                           width="100%"
//                           cellPadding={0}
//                           cellSpacing={0}
//                           border={0}
//                           style={{ backgroundColor: "#ffffff" }}
//                         >
//                           <tbody>
//                             <tr>
//                               <td style={{ padding: "35px 30px" }}>
//                                 <table
//                                   width="100%"
//                                   cellPadding={0}
//                                   cellSpacing={0}
//                                   border={0}
//                                 >
//                                   <tbody>
//                                     <tr>
//                                       <td width="28%" valign="top">
//                                         <img
//                                           src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/7c57c4a2-564b-4b83-62b0-761f834b5c00/public"
//                                           width={130}
//                                           alt="Zorba Stays"
//                                         />
//                                       </td>
//                                       {[
//                                         {
//                                           label: "Contact Us",
//                                           href: "https://zorbastays.com/contact",
//                                         },
//                                         {
//                                           label: "Experiences",
//                                           href: "https://zorbastays.com/experiences?experience=Wildlife%20Safari%20Experience",
//                                         },
//                                         {
//                                           label: "destinations",
//                                           href: "https://zorbastays.com/destinations?place=Goa",
//                                         },
//                                         {
//                                           label: "Travel Stories",
//                                           href: "https://zorbastays.com/blogs",
//                                         },
//                                       ].map(({ label, href }) => (
//                                         <td
//                                           key={label}
//                                           align="center"
//                                           valign="top"
//                                           style={{
//                                             fontSize: 14,
//                                             color: "#2d2d2d",
//                                             lineHeight: "28px",
//                                             textDecoration: "underline",
//                                             textUnderlineOffset: 4,
//                                           }}
//                                         >
//                                           <a
//                                             href={href}
//                                             target="_blank"
//                                             rel="noreferrer"
//                                             style={{
//                                               textDecoration: "none",
//                                               color: "#2d2d2d",
//                                             }}
//                                           >
//                                             {label}
//                                           </a>
//                                         </td>
//                                       ))}
//                                     </tr>
//                                     <tr>
//                                       <td colSpan={5} height={40} />
//                                     </tr>
//                                     <tr>
//                                       <td
//                                         colSpan={5}
//                                         style={{
//                                           fontSize: 12,
//                                           lineHeight: "24px",
//                                           color: "#444444",
//                                         }}
//                                       >
//                                         This email was intended for{" "}
//                                         {booking.email}. Zorba Stays is
//                                         registered in India.
//                                         <br />
//                                         Registered company number: 90167 72761.
//                                         Registered address: 308 - Business
//                                         Centrum, Amin Marg, Rajkot, India
//                                       </td>
//                                     </tr>
//                                   </tbody>
//                                 </table>
//                               </td>
//                             </tr>
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>
//                   </tbody>
//                 </table>
//               </td>
//             </tr>
//           </tbody>
//         </table>

//         {/* Attachments note */}
//         <table
//           width="100%"
//           cellPadding={0}
//           cellSpacing={0}
//           border={0}
//           style={{ marginTop: 30 }}
//         >
//           <tbody>
//             <tr>
//               <td
//                 style={{
//                   fontSize: 13,
//                   color: "#444444",
//                   lineHeight: "22px",
//                   padding: "16px 20px",
//                   backgroundColor: "#f5f4f0",
//                   borderLeft: "3px solid #2d2d2d",
//                 }}
//               >
//                 📎 Kindly take a moment to read through the House Rules and
//                 Terms &amp; Conditions mentioned below to help ensure a smooth
//                 and pleasant stay.
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>
//     </>
//   );
// };

// export default BookingConfirmPDF;

// ─── Usage ────────────────────────────────────────────────────────────────────
//
// 1. Resolve villa image before rendering (same logic as bookingConfirmMail):
//
// const destination_IMAGES = { goa: "...", ... };
// const DEFAULT_VILLA_IMG  = "...";
//
// 2. Render:
// {confirmBooking && (
//   <BookingConfirmPDF
//     booking={confirmBooking}
//     villaImg={resolvedVillaImg}
//     onClose={() => setConfirmBooking(null)}
//   />
// )}
